import React from "react";
import { Column } from "react-aria-components";
import clsx from "clsx";
import { ColumnResizer } from "react-aria-components";

import Ascending from "../../icons/ascending.svg?react";
import Descending from "../../icons/descending.svg?react";
import CanSort from "../../icons/can-sort.svg?react";

import { useTableContext } from "./table-context";
import styles from "./table-header-cell.module.css";

interface TableHeaderCellProps {
  children?: React.ReactNode;
  "data-testid"?: string;
  align?: "left" | "right" | "center";
  textValue?: string;
  isRowHeader?: boolean;
  isSortable?: boolean;
  id?: string;
  /** Defaults to the parent Table's isResizable value */
  isResizable?: boolean;
  display?: {
    // Width values are only applicable to resizable columns
    defaultWidth?: number;
    width?: number;
    minWidth?: number;
    maxWidth?: number;
  };
}

function TableHeaderCell({
  children,
  textValue,
  "data-testid": testId,
  isRowHeader = false,
  isSortable = false,
  isResizable: isResizableProp,
  id,
  align = "left",
  display,
}: TableHeaderCellProps) {
  const tableContext = useTableContext();
  const isResizable = isResizableProp ?? tableContext.isResizable;
  const widthProps = isResizable
    ? {
        defaultWidth: display?.defaultWidth,
        minWidth: display?.minWidth || 100,
        width: display?.width,
      }
    : { width: display?.width };
  return (
    <Column
      textValue={textValue}
      allowsSorting={isSortable}
      id={id}
      isRowHeader={isRowHeader}
      data-testid={testId}
      {...widthProps}
      className={clsx(styles.headerCell, styles[align], {
        [styles.isSortable]: isSortable,
      })}
    >
      {({ allowsSorting, sortDirection }) => (
        <div className={styles.cellWrapper}>
          <div className={styles.content}>{children || <>&nbsp;</>}</div>
          <div className={styles.actions}>
            {allowsSorting && (
              <div className={styles.sortIndicator}>
                <span aria-hidden="true" className={styles.sorted}>
                  {sortDirection === "ascending" ? (
                    <Ascending data-testid="ascending-icon" />
                  ) : (
                    <Descending data-testid="descending-icon" />
                  )}
                </span>
                <span className={styles.canSort}>
                  <CanSort />
                </span>
              </div>
            )}
            {isResizable && (
              <ColumnResizer data-testid="resizer" className={styles.resizer} />
            )}
          </div>
        </div>
      )}
    </Column>
  );
}

export default TableHeaderCell;
